'use client';

import { c as _c } from "react/compiler-runtime";
import type { MergeabilityStatus } from '@/lib/hooks/mergeability-status/get-mergeability-status.server';
import type { PrV2SummaryResponse } from '@/lib/pr-v2/types';
import { ChevronDown } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import type { PrimaryMergeAction } from './pr-v2-merge-choices';
import { PrV2MergeActionControls } from './pr-v2-merge-action-controls';
import { getCanQueueAutoMerge, getPrimaryButtonConfig } from './pr-v2-merge-choices';
import { parsePrV2Metadata } from './shared/pr-v2-metadata';
import { mapPrV2SummaryToPullRequest } from './shared/pr-v2-pull-request-adapter';
import { usePrV2MergeCommands } from './use-pr-v2-merge-commands';
import { ApprovalsSection } from '@/components/top-navigation/merge-dropdown/approvals-section';
import { CheckSection } from '@/components/top-navigation/merge-dropdown/check-section/check-section';
import { ConflictSection } from '@/components/top-navigation/merge-dropdown/conflict-section';
import { Header } from '@/components/top-navigation/merge-dropdown/header';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { KeyboardShortcut } from '@/components/ui/keyboard-shortcut';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePrV2AutoMergeActions } from '@/data/v2/pr/use-pr-v2-auto-merge-actions';
import { usePrV2MergeAction } from '@/data/v2/pr/use-pr-v2-merge-action';
import { usePrV2MergeabilityCapabilities } from '@/data/v2/pr/use-pr-v2-mergeability-capabilities';
import { usePrV2Reviews } from '@/data/v2/pr/use-pr-v2-reviews';
import { getMergeabilityStatusDotClasses } from '@/lib/hooks/mergeability-status/presentation';
import { useMergeabilityStatus } from '@/lib/hooks/mergeability-status/use-mergeability-status';
import { getEffectiveMergeMethodFromStatus } from '@/lib/pull-requests/merge-method';
import { toSelectableMergeMethods } from '@/lib/pull-requests/merge-method-preference';
import { useMergeMethodPreference } from '@/lib/pull-requests/use-merge-method-preference';
type PrV2MergeDropdownProps = {
  owner: string;
  repo: string;
  number: string;
  summary: PrV2SummaryResponse['pullRequest'];
  initialMergeabilityStatus: MergeabilityStatus | null;
};
export function PrV2MergeDropdown(t0) {
  const $ = _c(136);
  const {
    owner,
    repo,
    number,
    summary,
    initialMergeabilityStatus
  } = t0;
  const [open, setOpen] = useState(false);
  const [checkedBypassRules, setCheckedBypassRules] = useState(false);
  const [isMethodPickerOpen, setIsMethodPickerOpen] = useState(false);
  const primaryButtonRef = useRef(null);
  const methodPickerTriggerRef = useRef(null);
  let t1;
  if ($[0] !== owner || $[1] !== repo) {
    t1 = {
      owner,
      repo
    };
    $[0] = owner;
    $[1] = repo;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    preferredMergeMethod,
    selectMergeMethod
  } = useMergeMethodPreference(t1);
  let t2;
  if ($[3] !== initialMergeabilityStatus) {
    t2 = {
      initialData: initialMergeabilityStatus
    };
    $[3] = initialMergeabilityStatus;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    status: mergeabilityStatus
  } = useMergeabilityStatus(owner, repo, number, t2);
  const isPrOpen = !summary.merged && summary.state !== "closed";
  let t3;
  if ($[5] !== isPrOpen) {
    t3 = {
      enabled: isPrOpen
    };
    $[5] = isPrOpen;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const mergeabilityCapabilitiesQuery = usePrV2MergeabilityCapabilities(owner, repo, number, t3);
  const reviewsQuery = usePrV2Reviews(owner, repo, number);
  const mergeAction = usePrV2MergeAction(owner, repo, number);
  const autoMergeActions = usePrV2AutoMergeActions(owner, repo, number);
  let t4;
  if ($[7] !== summary.metadata) {
    t4 = parsePrV2Metadata(summary.metadata);
    $[7] = summary.metadata;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const metadata = t4;
  let t5;
  if ($[9] !== reviewsQuery.data) {
    t5 = reviewsQuery.data ?? [];
    $[9] = reviewsQuery.data;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== owner || $[12] !== repo || $[13] !== summary || $[14] !== t5) {
    t6 = mapPrV2SummaryToPullRequest(summary, owner, repo, t5);
    $[11] = owner;
    $[12] = repo;
    $[13] = summary;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const pr = t6;
  let t7;
  bb0: {
    if (!mergeabilityStatus) {
      t7 = null;
      break bb0;
    }
    const capabilities = mergeabilityCapabilitiesQuery.data;
    if (!capabilities) {
      t7 = mergeabilityStatus;
      break bb0;
    }
    let t8;
    if ($[16] !== capabilities.canBypassProtections || $[17] !== capabilities.canEnableAutoMerge || $[18] !== capabilities.isAdmin || $[19] !== capabilities.viewerPermission || $[20] !== mergeabilityStatus.viewer) {
      t8 = {
        ...mergeabilityStatus.viewer,
        permission: capabilities.viewerPermission,
        isAdmin: capabilities.isAdmin,
        canBypassProtections: capabilities.canBypassProtections,
        canEnableAutoMerge: capabilities.canEnableAutoMerge
      };
      $[16] = capabilities.canBypassProtections;
      $[17] = capabilities.canEnableAutoMerge;
      $[18] = capabilities.isAdmin;
      $[19] = capabilities.viewerPermission;
      $[20] = mergeabilityStatus.viewer;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] !== mergeabilityStatus || $[23] !== t8) {
      t9 = {
        ...mergeabilityStatus,
        viewer: t8
      };
      $[22] = mergeabilityStatus;
      $[23] = t8;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    t7 = t9;
  }
  const effectiveMergeabilityStatus = t7;
  const t8 = effectiveMergeabilityStatus?.autoMerge.allowedMethods;
  const t9 = effectiveMergeabilityStatus?.requiresLinearHistory ?? false;
  const t10 = preferredMergeMethod ?? "merge";
  let t11;
  if ($[25] !== t10 || $[26] !== t8 || $[27] !== t9) {
    t11 = getEffectiveMergeMethodFromStatus(t8, t9, t10);
    $[25] = t10;
    $[26] = t8;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const effectiveMergeMethod = t11;
  let t12;
  if ($[29] !== effectiveMergeabilityStatus?.autoMerge.allowedMethods || $[30] !== effectiveMergeabilityStatus?.requiresLinearHistory) {
    const methods = toSelectableMergeMethods(effectiveMergeabilityStatus?.autoMerge.allowedMethods);
    t12 = effectiveMergeabilityStatus?.requiresLinearHistory ? methods.filter(_temp) : methods;
    $[29] = effectiveMergeabilityStatus?.autoMerge.allowedMethods;
    $[30] = effectiveMergeabilityStatus?.requiresLinearHistory;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const selectableMergeMethods = t12;
  const isPending = mergeAction.isPending || autoMergeActions.isPending;
  let t13;
  if ($[32] !== effectiveMergeabilityStatus || $[33] !== summary.draft) {
    t13 = effectiveMergeabilityStatus ? getCanQueueAutoMerge(effectiveMergeabilityStatus, summary.draft) : false;
    $[32] = effectiveMergeabilityStatus;
    $[33] = summary.draft;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const canQueueAutoMerge = t13;
  const canBypassRules = Boolean(effectiveMergeabilityStatus?.viewer.isAdmin && effectiveMergeabilityStatus.canAdminsBypassRules && effectiveMergeabilityStatus.mergeQueue !== "queued" && isPrOpen);
  const isAdminBypassActive = checkedBypassRules && canBypassRules;
  let t14;
  if ($[35] !== canQueueAutoMerge || $[36] !== effectiveMergeMethod || $[37] !== effectiveMergeabilityStatus || $[38] !== isPending || $[39] !== owner || $[40] !== repo || $[41] !== summary.draft) {
    t14 = effectiveMergeabilityStatus && {
      owner,
      repo,
      status: effectiveMergeabilityStatus,
      effectiveMergeMethod,
      isPending,
      isDraft: summary.draft,
      canQueueAutoMerge
    };
    $[35] = canQueueAutoMerge;
    $[36] = effectiveMergeMethod;
    $[37] = effectiveMergeabilityStatus;
    $[38] = isPending;
    $[39] = owner;
    $[40] = repo;
    $[41] = summary.draft;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const buttonInput = t14;
  let t15;
  if ($[43] !== buttonInput || $[44] !== isAdminBypassActive) {
    t15 = buttonInput ? getPrimaryButtonConfig({
      ...buttonInput,
      isAdminBypass: isAdminBypassActive
    }) : null;
    $[43] = buttonInput;
    $[44] = isAdminBypassActive;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  const primaryButtonConfig = t15;
  const mergeOptionsLabel = effectiveMergeabilityStatus ? `${effectiveMergeabilityStatus.humanReadableMergeStatus}. View merge options` : "View merge options";
  let t16;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      setOpen(false);
      setIsMethodPickerOpen(false);
    };
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const closeMenu = t16;
  let t17;
  if ($[47] !== autoMergeActions || $[48] !== effectiveMergeabilityStatus || $[49] !== mergeAction || $[50] !== metadata || $[51] !== summary.headSha || $[52] !== summary.number || $[53] !== summary.title) {
    t17 = action => {
      if (!effectiveMergeabilityStatus) {
        return;
      }
      closeMenu();
      if (action.kind === "view-queue") {
        if (action.url) {
          window.open(action.url, "_blank", "noopener,noreferrer");
        }
        return;
      }
      if (action.kind === "auto-merge") {
        autoMergeActions.setAutoMerge({
          action: action.toggle,
          pullRequestId: effectiveMergeabilityStatus.pullRequestId,
          mergeMethod: action.mergeMethod
        });
        return;
      }
      mergeAction.mutate({
        title: summary.title ?? `PR #${summary.number}`,
        body: metadata.body ?? "",
        sha: summary.headSha,
        merge_method: action.mergeMethod,
        merge_action: action.mergeAction
      });
    };
    $[47] = autoMergeActions;
    $[48] = effectiveMergeabilityStatus;
    $[49] = mergeAction;
    $[50] = metadata;
    $[51] = summary.headSha;
    $[52] = summary.number;
    $[53] = summary.title;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  const runMergeAction = t17;
  let t18;
  if ($[55] !== buttonInput || $[56] !== isPrOpen || $[57] !== runMergeAction || $[58] !== selectMergeMethod || $[59] !== selectableMergeMethods || $[60] !== summary.number) {
    t18 = {
      buttonInput,
      isPrOpen,
      number: summary.number,
      selectableMergeMethods,
      selectMergeMethod,
      runMergeAction
    };
    $[55] = buttonInput;
    $[56] = isPrOpen;
    $[57] = runMergeAction;
    $[58] = selectMergeMethod;
    $[59] = selectableMergeMethods;
    $[60] = summary.number;
    $[61] = t18;
  } else {
    t18 = $[61];
  }
  usePrV2MergeCommands(t18);
  let t19;
  let t20;
  let t21;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = event => {
      event.preventDefault();
      setOpen(_temp2);
      setIsMethodPickerOpen(false);
    };
    t20 = {
      enableOnFormTags: false,
      enabled: true
    };
    t21 = [];
    $[62] = t19;
    $[63] = t20;
    $[64] = t21;
  } else {
    t19 = $[62];
    t20 = $[63];
    t21 = $[64];
  }
  useHotkeys("m", t19, t20, t21);
  let t22;
  if ($[65] !== primaryButtonConfig || $[66] !== runMergeAction) {
    t22 = event_0 => {
      event_0.preventDefault();
      if (primaryButtonConfig && !primaryButtonConfig.disabled) {
        runMergeAction(primaryButtonConfig.action);
      }
    };
    $[65] = primaryButtonConfig;
    $[66] = runMergeAction;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      enableOnFormTags: false,
      enabled: true
    };
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== effectiveMergeMethod || $[70] !== effectiveMergeabilityStatus || $[71] !== isAdminBypassActive || $[72] !== isPending || $[73] !== primaryButtonConfig || $[74] !== summary.draft) {
    t24 = [effectiveMergeabilityStatus, effectiveMergeMethod, isAdminBypassActive, isPending, primaryButtonConfig, summary.draft];
    $[69] = effectiveMergeMethod;
    $[70] = effectiveMergeabilityStatus;
    $[71] = isAdminBypassActive;
    $[72] = isPending;
    $[73] = primaryButtonConfig;
    $[74] = summary.draft;
    $[75] = t24;
  } else {
    t24 = $[75];
  }
  useHotkeys("shift+m", t22, t23, t24);
  if (!isPrOpen || !effectiveMergeabilityStatus || !primaryButtonConfig) {
    return null;
  }
  let t25;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = nextOpen => {
      setOpen(nextOpen);
      if (!nextOpen) {
        setIsMethodPickerOpen(false);
      }
    };
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  let t26;
  if ($[77] !== effectiveMergeabilityStatus) {
    t26 = getMergeabilityStatusDotClasses(effectiveMergeabilityStatus);
    $[77] = effectiveMergeabilityStatus;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  const t27 = `mr-2 size-2 shrink-0 rounded-full ${t26}`;
  let t28;
  if ($[79] !== t27) {
    t28 = <span aria-hidden="true" data-testid="pr-v2-merge-status-dot" className={t27} />;
    $[79] = t27;
    $[80] = t28;
  } else {
    t28 = $[80];
  }
  const t29 = canQueueAutoMerge ? "Auto-merge" : "Merge";
  let t30;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <ChevronDown className="ml-1.5 size-3.5 opacity-60" />;
    $[81] = t30;
  } else {
    t30 = $[81];
  }
  let t31;
  if ($[82] !== mergeOptionsLabel || $[83] !== t28 || $[84] !== t29) {
    t31 = <TooltipTrigger asChild={true}><DropdownMenuTrigger asChild={true}><Button size="sm" variant="outline" className="gap-0 max-md:h-10 max-md:w-full" aria-label={mergeOptionsLabel}>{t28}{t29}{t30}</Button></DropdownMenuTrigger></TooltipTrigger>;
    $[82] = mergeOptionsLabel;
    $[83] = t28;
    $[84] = t29;
    $[85] = t31;
  } else {
    t31 = $[85];
  }
  let t32;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <KeyboardShortcut>M</KeyboardShortcut>;
    $[86] = t32;
  } else {
    t32 = $[86];
  }
  let t33;
  if ($[87] !== mergeOptionsLabel) {
    t33 = <TooltipContent><div className="flex items-center gap-2">{mergeOptionsLabel}{t32}</div></TooltipContent>;
    $[87] = mergeOptionsLabel;
    $[88] = t33;
  } else {
    t33 = $[88];
  }
  let t34;
  if ($[89] !== t31 || $[90] !== t33) {
    t34 = <TooltipProvider><Tooltip>{t31}{t33}</Tooltip></TooltipProvider>;
    $[89] = t31;
    $[90] = t33;
    $[91] = t34;
  } else {
    t34 = $[91];
  }
  let t35;
  if ($[92] !== isMethodPickerOpen) {
    t35 = event_1 => {
      if (!isMethodPickerOpen) {
        return;
      }
      event_1.preventDefault();
      setIsMethodPickerOpen(false);
      methodPickerTriggerRef.current?.focus();
    };
    $[92] = isMethodPickerOpen;
    $[93] = t35;
  } else {
    t35 = $[93];
  }
  let t36;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = event_2 => {
      if (event_2.key === "Tab" && event_2.target === event_2.currentTarget) {
        event_2.preventDefault();
        primaryButtonRef.current?.focus();
      }
    };
    $[94] = t36;
  } else {
    t36 = $[94];
  }
  let t37;
  if ($[95] !== effectiveMergeabilityStatus) {
    t37 = <Header mergeabilityStatus={effectiveMergeabilityStatus} />;
    $[95] = effectiveMergeabilityStatus;
    $[96] = t37;
  } else {
    t37 = $[96];
  }
  let t38;
  if ($[97] !== effectiveMergeabilityStatus || $[98] !== pr) {
    t38 = <div className="flex items-center justify-between py-2"><ApprovalsSection pr={pr} mergeabilityStatus={effectiveMergeabilityStatus} /></div>;
    $[97] = effectiveMergeabilityStatus;
    $[98] = pr;
    $[99] = t38;
  } else {
    t38 = $[99];
  }
  let t39;
  if ($[100] !== effectiveMergeabilityStatus) {
    t39 = <ConflictSection mergeabilityStatus={effectiveMergeabilityStatus} />;
    $[100] = effectiveMergeabilityStatus;
    $[101] = t39;
  } else {
    t39 = $[101];
  }
  let t40;
  if ($[102] !== effectiveMergeabilityStatus || $[103] !== summary.headSha) {
    t40 = <CheckSection mergeabilityStatus={effectiveMergeabilityStatus} headSha={summary.headSha} />;
    $[102] = effectiveMergeabilityStatus;
    $[103] = summary.headSha;
    $[104] = t40;
  } else {
    t40 = $[104];
  }
  let t41;
  if ($[105] !== t38 || $[106] !== t39 || $[107] !== t40) {
    t41 = <div className="flex flex-col gap-y-1 px-4">{t38}{t39}{t40}</div>;
    $[105] = t38;
    $[106] = t39;
    $[107] = t40;
    $[108] = t41;
  } else {
    t41 = $[108];
  }
  let t42;
  if ($[109] !== primaryButtonConfig.action || $[110] !== runMergeAction) {
    t42 = () => runMergeAction(primaryButtonConfig.action);
    $[109] = primaryButtonConfig.action;
    $[110] = runMergeAction;
    $[111] = t42;
  } else {
    t42 = $[111];
  }
  const t43 = selectableMergeMethods.length > 1 && primaryButtonConfig.usesMergeMethod;
  let t44;
  if ($[112] !== canBypassRules || $[113] !== checkedBypassRules || $[114] !== isMethodPickerOpen || $[115] !== isPending || $[116] !== t43) {
    t44 = {
      canPickMergeMethod: t43,
      isPending,
      isMethodPickerOpen,
      canBypassRules,
      checkedBypassRules
    };
    $[112] = canBypassRules;
    $[113] = checkedBypassRules;
    $[114] = isMethodPickerOpen;
    $[115] = isPending;
    $[116] = t43;
    $[117] = t44;
  } else {
    t44 = $[117];
  }
  let t45;
  if ($[118] !== effectiveMergeMethod || $[119] !== primaryButtonConfig || $[120] !== selectMergeMethod || $[121] !== selectableMergeMethods || $[122] !== t42 || $[123] !== t44) {
    t45 = <PrV2MergeActionControls primaryButtonRef={primaryButtonRef} methodPickerTriggerRef={methodPickerTriggerRef} primaryButtonConfig={primaryButtonConfig} primaryAction={t42} closeMenu={closeMenu} state={t44} setIsMethodPickerOpen={setIsMethodPickerOpen} selectableMergeMethods={selectableMergeMethods} effectiveMergeMethod={effectiveMergeMethod} selectMergeMethod={selectMergeMethod} setCheckedBypassRules={setCheckedBypassRules} />;
    $[118] = effectiveMergeMethod;
    $[119] = primaryButtonConfig;
    $[120] = selectMergeMethod;
    $[121] = selectableMergeMethods;
    $[122] = t42;
    $[123] = t44;
    $[124] = t45;
  } else {
    t45 = $[124];
  }
  let t46;
  if ($[125] !== t41 || $[126] !== t45) {
    t46 = <div className="min-h-0 flex-1 overflow-y-auto">{t41}{t45}</div>;
    $[125] = t41;
    $[126] = t45;
    $[127] = t46;
  } else {
    t46 = $[127];
  }
  let t47;
  if ($[128] !== t35 || $[129] !== t37 || $[130] !== t46) {
    t47 = <DropdownMenuContent align="end" collisionPadding={8} className="w-[min(24rem,calc(100vw-1rem))] border-border bg-black text-white max-h-[80svh] flex flex-col gap-y-3 text-sm py-4 overflow-hidden" onEscapeKeyDown={t35} onKeyDown={t36}>{t37}{t46}</DropdownMenuContent>;
    $[128] = t35;
    $[129] = t37;
    $[130] = t46;
    $[131] = t47;
  } else {
    t47 = $[131];
  }
  let t48;
  if ($[132] !== open || $[133] !== t34 || $[134] !== t47) {
    t48 = <div className="flex justify-end max-md:flex-1"><DropdownMenu modal={false} open={open} onOpenChange={t25}>{t34}{t47}</DropdownMenu></div>;
    $[132] = open;
    $[133] = t34;
    $[134] = t47;
    $[135] = t48;
  } else {
    t48 = $[135];
  }
  return t48;
}
function _temp2(current) {
  return !current;
}
function _temp(method) {
  return method !== "merge";
}